import React from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import storeDetailsNotificationsService from '../services/storeDetailsNotificationsService';
import {
    buildStoreDetailsCreateNotificationPayload,
    getStoreDetailsNotificationStatuses,
    mapStoreDetailsNotificationRows,
} from '../mappers/storeDetailsNotificationsMapper';
import type {
    StoreDetailsNotification,
    StoreDetailsSendNotificationFormValues,
} from '../../../../types/features/storeDetails/notifications';
import type { StoreDetailsTab } from '../../../../types/features/storeDetails/page';

const STORE_DETAILS_SEND_NOTIFICATION_DEFAULT_VALUES: StoreDetailsSendNotificationFormValues = {
    title: '',
    message: '',
    auditId: '',
    critical: false,
    userIds: [],
    channel: 'in_app',
};

type UseStoreDetailsNotificationsParams = {
    activeTab: StoreDetailsTab;
    storeId?: string;
};

export const useStoreDetailsNotifications = ({
    activeTab,
    storeId,
}: UseStoreDetailsNotificationsParams) => {
    const [notifications, setNotifications] = React.useState<StoreDetailsNotification[]>([]);
    const [notificationStatuses, setNotificationStatuses] = React.useState<string[]>([]);
    const [notificationsLoading, setNotificationsLoading] = React.useState(false);
    const [notificationsError, setNotificationsError] = React.useState('');
    const [sendNotificationDialogOpen, setSendNotificationDialogOpen] = React.useState(false);
    const [sendNotificationSubmitting, setSendNotificationSubmitting] = React.useState(false);
    const [sendNotificationError, setSendNotificationError] = React.useState('');
    const [sendNotificationValues, setSendNotificationValues] = React.useState<StoreDetailsSendNotificationFormValues>(
        STORE_DETAILS_SEND_NOTIFICATION_DEFAULT_VALUES,
    );

    const loadNotifications = React.useCallback(async () => {
        if (!storeId) return;

        try {
            setNotificationsLoading(true);
            setNotificationsError('');
            const response = await storeDetailsNotificationsService.getStoreNotifications(storeId);
            setNotifications(mapStoreDetailsNotificationRows(response));
            setNotificationStatuses(getStoreDetailsNotificationStatuses(response));
        } catch (error) {
            console.error('Error loading store notifications:', error);
            setNotifications([]);
            setNotificationStatuses([]);
            setNotificationsError('Unable to load notifications. Please try again.');
        } finally {
            setNotificationsLoading(false);
        }
    }, [storeId]);

    React.useEffect(() => {
        if (activeTab !== 'notifications' || !storeId) return;
        void loadNotifications();
    }, [activeTab, loadNotifications, storeId]);

    const handleOpenSendNotificationDialog = React.useCallback(() => {
        setSendNotificationError('');
        setSendNotificationDialogOpen(true);
    }, []);

    const handleCloseSendNotificationDialog = React.useCallback(() => {
        if (sendNotificationSubmitting) return;
        setSendNotificationError('');
        setSendNotificationDialogOpen(false);
    }, [sendNotificationSubmitting]);

    const handleSendNotificationTitleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSendNotificationValues((previous) => ({ ...previous, title: event.target.value }));
    }, []);

    const handleSendNotificationMessageChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSendNotificationValues((previous) => ({ ...previous, message: event.target.value }));
    }, []);

    const handleSendNotificationAuditIdChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSendNotificationValues((previous) => ({ ...previous, auditId: event.target.value }));
    }, []);

    const handleSendNotificationCriticalChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSendNotificationValues((previous) => ({ ...previous, critical: event.target.checked }));
    }, []);

    const handleSendNotificationUserChange = React.useCallback((event: SelectChangeEvent<string[]>) => {
        const nextUserIds = typeof event.target.value === 'string'
            ? event.target.value.split(',')
            : event.target.value;
        setSendNotificationValues((previous) => ({ ...previous, userIds: nextUserIds }));
    }, []);

    const handleSendNotificationChannelChange = React.useCallback((event: SelectChangeEvent) => {
        setSendNotificationValues((previous) => ({
            ...previous,
            channel: event.target.value as StoreDetailsSendNotificationFormValues['channel'],
        }));
    }, []);

    const sendNotificationDisabled = !storeId
        || !sendNotificationValues.title.trim()
        || !sendNotificationValues.message.trim()
        || sendNotificationValues.userIds.length === 0
        || sendNotificationSubmitting;

    const handleSendNotification = React.useCallback(async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!storeId || sendNotificationDisabled) return;

        try {
            setSendNotificationSubmitting(true);
            setSendNotificationError('');
            await storeDetailsNotificationsService.createStoreNotification(
                buildStoreDetailsCreateNotificationPayload(storeId, sendNotificationValues),
            );
            setSendNotificationValues(STORE_DETAILS_SEND_NOTIFICATION_DEFAULT_VALUES);
            setSendNotificationDialogOpen(false);
            await loadNotifications();
        } catch (error) {
            console.error('Error sending store notification:', error);
            setSendNotificationError('Unable to send notification. Please try again.');
        } finally {
            setSendNotificationSubmitting(false);
        }
    }, [loadNotifications, sendNotificationDisabled, sendNotificationValues, storeId]);

    return {
        notifications,
        notificationStatuses,
        notificationsLoading,
        notificationsError,
        sendNotificationValues,
        sendNotificationDialogOpen,
        sendNotificationSubmitting,
        sendNotificationError,
        sendNotificationDisabled,
        loadNotifications,
        handleOpenSendNotificationDialog,
        handleCloseSendNotificationDialog,
        handleSendNotificationTitleChange,
        handleSendNotificationMessageChange,
        handleSendNotificationAuditIdChange,
        handleSendNotificationCriticalChange,
        handleSendNotificationUserChange,
        handleSendNotificationChannelChange,
        handleSendNotification,
    };
};
